import { useState } from 'react';
import { Link } from 'react-router-dom';
import SelfDepositModal from './SelfDepositModal';
import ChangePasswordModal from './ChangePasswordModal';
import { formatDisplayUserId } from '../utils/userIdFormat';
import { useGetUserProfileQuery } from '../services/auth/authApi';
import { getDomainLogo } from '../utils/domainConfig';

const logo = getDomainLogo();

const RefreshIcon = () => (
  <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 17 17" height="16" width="16">
    <path d="M6 8h-6v-6h1v4.109c1.013-3.193 4.036-5.484 7.5-5.484 3.506 0 6.621 2.36 7.574 5.739l-0.963 0.271c-0.832-2.95-3.551-5.011-6.611-5.011-3.226 0.001-6.016 2.276-6.708 5.376h4.208v1zM11 9v1h4.208c-0.693 3.101-3.479 5.375-6.708 5.375-3.062 0-5.78-2.061-6.611-5.011l-0.963 0.271c0.952 3.379 4.067 5.739 7.574 5.739 3.459 0 6.475-2.28 7.5-5.482v4.108h1v-6h-6z"/>
  </svg>
);

const ChevronIcon = ({ open }) => (
  <svg
    className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
    viewBox="0 0 24 24" fill="currentColor"
  >
    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
  </svg>
);

const DepositIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="8 12 12 16 16 12"/>
    <line x1="12" y1="8" x2="12" y2="16"/>
  </svg>
);

const KeyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
  </svg>
);

const LogoutIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);

const MenuIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
    <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
  </svg>
);

const UserAvatar = ({ name }) => {
  const initials = name
    ? name.replace(/[^a-zA-Z0-9]/g, '').slice(0, 2).toUpperCase()
    : 'U';
  return (
    <span className="w-7 h-7 rounded-full bg-gradient-to-br from-[var(--accent)] to-[var(--accent-3)] text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0 shadow-[0_0_12px_rgba(192,43,100,0.5)] ring-1 ring-black/10">
      {initials}
    </span>
  );
};

const Header = ({ onMenuClick, sidebarCollapsed }) => {
  const { data: user, isLoading: userLoading } = useGetUserProfileQuery();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [depositModalOpen, setDepositModalOpen] = useState(false);
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);

  const isSuperAdmin = user?.user?.userType === 'SuperAdmin';
  const username = userLoading ? '…' : (user?.user?.userId || 'User');
  const displayName = userLoading ? '…' : (user?.user?.username || 'User');
  const userType = user?.user?.userType || '';

  const handleLogout = () => {
    localStorage.clear();
    window.location.replace('/login');
    setDropdownOpen(false);
  };

  const handleRefresh = () => window.location.reload();

  const handleDepositClick = () => { setDropdownOpen(false); setDepositModalOpen(true); };
  const handleChangePasswordClick = () => { setDropdownOpen(false); setChangePasswordModalOpen(true); };

  return (
    <>
      <header
        className={`fixed top-0 right-0 h-16 glass-dark border-b border-black/10 flex items-center justify-between px-4 z-30 transition-all duration-300
          ${sidebarCollapsed ? 'lg:left-20' : 'lg:left-64'} left-0`}
      >
        {/* Left — hamburger + mobile logo */}
        <div className="flex items-center gap-3">
          <button
            onClick={onMenuClick}
            className="text-[var(--chrome-text-2)] hover:text-[var(--chrome-text)] lg:hidden p-1.5 rounded-lg hover:bg-black/5 transition-all"
          >
            <MenuIcon />
          </button>
          <Link to="/dashboard" className="lg:hidden">
            <img src={logo} alt="logo" className="h-10 w-10" />
          </Link>
        </div>

        {/* Right — actions */}
        <div className="flex items-center gap-2 ml-auto">
          {/* Refresh */}
          <button
            onClick={handleRefresh}
            title="Refresh"
            className="text-[var(--chrome-text-2)] hover:text-[var(--chrome-text)] p-2 rounded-lg hover:bg-black/5 transition-all"
          >
            <RefreshIcon />
          </button>

          {/* Divider */}
          <span className="w-px h-5 bg-black/15" />

          {/* User dropdown */}
          <div className="relative">
            <button
              onClick={() => setDropdownOpen(o => !o)}
              className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg hover:bg-black/5 transition-all text-[var(--chrome-text)]"
            >
              <UserAvatar name={username} />
              <span className="text-sm font-medium text-[var(--chrome-text)] hidden sm:block">
                {username}
              </span>
              <ChevronIcon open={dropdownOpen} />
            </button>

            {dropdownOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setDropdownOpen(false)} />
                <div className="absolute right-0 top-full mt-2 w-56 glass-dark rounded-xl shadow-2xl z-20 overflow-hidden animate-slideDown">
                  {/* Profile header */}
                  <div className="px-4 py-3 border-b border-black/10 flex items-center gap-3">
                    <UserAvatar name={username} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[var(--chrome-text)] truncate">{displayName}</p>
                      <p className="text-[11px] text-[var(--chrome-accent)] truncate">{formatDisplayUserId(username)}</p>
                      {userType && (
                        <p className="text-[10px] text-[var(--chrome-text-3)] truncate mt-0.5">{userType}</p>
                      )}
                    </div>
                  </div>

                  <div className="py-1.5">
                    {!userLoading && isSuperAdmin && (
                      <>
                        <button
                          onClick={handleDepositClick}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[var(--chrome-text-2)] hover:text-[var(--chrome-text)] hover:bg-black/5 transition-all"
                        >
                          <span className="text-[var(--chrome-accent)]"><DepositIcon /></span>
                          Deposit
                        </button>
                        <button
                          onClick={handleChangePasswordClick}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[var(--chrome-text-2)] hover:text-[var(--chrome-text)] hover:bg-black/5 transition-all"
                        >
                          <span className="text-[var(--chrome-text-2)]"><KeyIcon /></span>
                          Change Password
                        </button>
                        <div className="my-1 border-t border-black/10" />
                      </>
                    )}
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-500/10 transition-all"
                    >
                      <LogoutIcon />
                      Logout
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <SelfDepositModal isOpen={depositModalOpen} onClose={() => setDepositModalOpen(false)} />
      <ChangePasswordModal isOpen={changePasswordModalOpen} onClose={() => setChangePasswordModalOpen(false)} />
    </>
  );
};

export default Header;
